import {AffineCoefficients, GeoCoordinate, Point, ReferencePoint, Triangle} from '../../types'

type CoefficientMatrix = {
  row1: [number, number, number]
  row2: [number, number, number]
  row3: [number, number, number]
}

const calculateDeterminant = (matrix: CoefficientMatrix): number => {
  const [a, b, c] = matrix.row1
  const [d, e, f] = matrix.row2
  const [g, h, i] = matrix.row3

  const cofactorA = e * i - f * h
  const cofactorB = d * i - f * g
  const cofactorC = d * h - e * g

  return a * cofactorA - b * cofactorB + c * cofactorC
}

const replaceColumn = (
  matrix: CoefficientMatrix,
  columnIndex: 0 | 1 | 2,
  replacementValues: [number, number, number]
): CoefficientMatrix => {
  const replaceValueInRow = (
    row: [number, number, number],
    newValue: number
  ): [number, number, number] => {
    const updatedRow: [number, number, number] = [...row]
    updatedRow[columnIndex] = newValue
    return updatedRow
  }

  return {
    row1: replaceValueInRow(matrix.row1, replacementValues[0]),
    row2: replaceValueInRow(matrix.row2, replacementValues[1]),
    row3: replaceValueInRow(matrix.row3, replacementValues[2])
  }
}

/**
 * Cramer's rule: x[i] = det(A with column i replaced by b) / det(A)
 * @throws Error if matrix is singular (points are collinear)
 */
const solveCramersRule = (
  matrix: CoefficientMatrix,
  rightHandSide: [number, number, number]
): [number, number, number] => {
  const det = calculateDeterminant(matrix)

  if (Math.abs(det) < 1e-10) {
    throw new Error('Cannot solve: reference points may be collinear')
  }

  const x = calculateDeterminant(replaceColumn(matrix, 0, rightHandSide)) / det
  const y = calculateDeterminant(replaceColumn(matrix, 1, rightHandSide)) / det
  const z = calculateDeterminant(replaceColumn(matrix, 2, rightHandSide)) / det

  return [x, y, z]
}

/**
 * Solves for affine coefficients that map (lon, lat) → (svgX, svgY)
 */
export const calculateAffineCoefficients = (
  points: [ReferencePoint, ReferencePoint, ReferencePoint]
): AffineCoefficients => {
  const [point1, point2, point3] = points

  // Allows translation offset; without it, transform is forced through origin
  const TRANSLATION_TERM = 1

  const geoMatrix: CoefficientMatrix = {
    row1: [point1.geo.longitude, point1.geo.latitude, TRANSLATION_TERM],
    row2: [point2.geo.longitude, point2.geo.latitude, TRANSLATION_TERM],
    row3: [point3.geo.longitude, point3.geo.latitude, TRANSLATION_TERM]
  }

  const svgXValues: [number, number, number] = [point1.svg.x, point2.svg.x, point3.svg.x]
  const [longitudeToX, latitudeToX, translateX] = solveCramersRule(geoMatrix, svgXValues)

  const svgYValues: [number, number, number] = [point1.svg.y, point2.svg.y, point3.svg.y]
  const [longitudeToY, latitudeToY, translateY] = solveCramersRule(geoMatrix, svgYValues)

  return {longitudeToX, latitudeToX, translateX, longitudeToY, latitudeToY, translateY}
}

export const transformGeoToSvg = (geo: GeoCoordinate, coefficients: AffineCoefficients): Point => {
  const svgX =
    coefficients.longitudeToX * geo.longitude +
    coefficients.latitudeToX * geo.latitude +
    coefficients.translateX
  const svgY =
    coefficients.longitudeToY * geo.longitude +
    coefficients.latitudeToY * geo.latitude +
    coefficients.translateY

  return {
    x: Math.round(svgX),
    y: Math.round(svgY)
  }
}

/**
 * Signed area via cross product: (B-A) × (C-A)
 * Positive = counter-clockwise, Negative = clockwise
 */
const signedTriangleArea = (
  vertexA: GeoCoordinate,
  vertexB: GeoCoordinate,
  vertexC: GeoCoordinate
): number => {
  const edgeAB_Lon = vertexB.longitude - vertexA.longitude
  const edgeAB_Lat = vertexB.latitude - vertexA.latitude
  const edgeAC_Lon = vertexC.longitude - vertexA.longitude
  const edgeAC_Lat = vertexC.latitude - vertexA.latitude

  return edgeAB_Lon * edgeAC_Lat - edgeAC_Lon * edgeAB_Lat
}

const BARYCENTRIC_EDGE_TOLERANCE = 0.01

/**
 * Uses barycentric coordinates: point is inside if all area ratios are non-negative.
 */
const isPointInTriangle = (point: GeoCoordinate, triangle: Triangle): boolean => {
  const [vertexA, vertexB, vertexC] = triangle.points.map((refPoint) => refPoint.geo)

  const totalArea = signedTriangleArea(vertexA, vertexB, vertexC)
  if (Math.abs(totalArea) < 1e-10) return false // Degenerate triangle

  const s = signedTriangleArea(vertexA, vertexB, point) / totalArea
  const t = signedTriangleArea(vertexB, vertexC, point) / totalArea
  const u = signedTriangleArea(vertexC, vertexA, point) / totalArea

  return (
    s >= -BARYCENTRIC_EDGE_TOLERANCE &&
    t >= -BARYCENTRIC_EDGE_TOLERANCE &&
    u >= -BARYCENTRIC_EDGE_TOLERANCE
  )
}

const getTriangleCentroid = (triangle: Triangle): GeoCoordinate => {
  const [vertexA, vertexB, vertexC] = triangle.points.map((refPoint) => refPoint.geo)

  return {
    longitude: (vertexA.longitude + vertexB.longitude + vertexC.longitude) / 3,
    latitude: (vertexA.latitude + vertexB.latitude + vertexC.latitude) / 3
  }
}

const distanceBetween = (fromPoint: GeoCoordinate, toPoint: GeoCoordinate): number => {
  const deltaLongitude = fromPoint.longitude - toPoint.longitude
  const deltaLatitude = fromPoint.latitude - toPoint.latitude

  return Math.sqrt(deltaLongitude * deltaLongitude + deltaLatitude * deltaLatitude)
}

/**
 * Returns the triangle containing the point, or the nearest one by centroid distance.
 */
export const findBestTriangle = (point: GeoCoordinate, triangles: Triangle[]): Triangle => {
  const containingTriangle = triangles.find((triangle) => isPointInTriangle(point, triangle))
  if (containingTriangle) {
    return containingTriangle
  }

  const trianglesWithDistance = triangles.map((triangle) => ({
    triangle,
    distance: distanceBetween(point, getTriangleCentroid(triangle))
  }))

  const nearest = trianglesWithDistance.reduce((closest, current) =>
    current.distance < closest.distance ? current : closest
  )

  return nearest.triangle
}
